import { useQuery } from '@tanstack/react-query';
import { BillingPlan, BillingPlanSearchRequest } from 'types/billingplan';
import { request } from 'utils/api';

type UseChargeCardBillingPlansProps = {
  currency?: string;
  billingPlanId?: string;
  onlyPrimary?: boolean;
  restrictBillingPlanId?: string;
  accountId?: string;
};

export function useChargeCardBillingPlans({
  currency,
  billingPlanId,
  onlyPrimary,
  restrictBillingPlanId,
  accountId,
}: UseChargeCardBillingPlansProps = {}) {
  return useQuery<BillingPlan[], unknown, BillingPlan[]>({
    queryKey: [
      'charge-card-billing-plans',
      currency,
      billingPlanId,
      onlyPrimary,
      restrictBillingPlanId,
      accountId,
    ],
    queryFn: async () => {
      const req: BillingPlanSearchRequest = {
        providerContext: 'msp',
        currency: currency || undefined,
      };

      if (restrictBillingPlanId && accountId) {
        throw new Error(
          'Please set restrictBillingPlanId or accountId, but not both'
        );
      }

      // Since api will override id filter when accountId is set, we set one or the other.
      // @see https://github.com/e-flux-platform/e-flux/blob/b6563cb2e5887039ac2aa43576c599c7ba34e5df/services/api/src/v1/billing-plans.ts#L331
      if (restrictBillingPlanId) {
        req.id = restrictBillingPlanId;
      } else if (accountId) {
        req.accountId = accountId;
      }

      return request({
        method: 'POST',
        path: '/1/billing-plans/search',
        body: req,
      }).then(
        ({
          data,
          meta,
        }: {
          data: BillingPlan[];
          meta: { restrictedOnMspPlans: boolean };
        }) => {
          return data
            .filter((c) => {
              if (restrictBillingPlanId) {
                return c.id === restrictBillingPlanId;
              }
              if (meta.restrictedOnMspPlans) {
                return true;
              }
              if (!c.isPublic) {
                return false;
              }
              if (onlyPrimary && !restrictBillingPlanId && !c.isPrimary) {
                return false;
              }
              return true;
            })
            .sort((a, b) => b.priority - a.priority);
        }
      );
    },
    staleTime: Infinity,
    networkMode: 'always',
  });
}
